import { CAlert, CButton, CCol, CForm, CFormLabel } from '@coreui/react'
import { faCopy, faDownload, faEdit, faTrashAlt, faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useId, useRef, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { GenericConfirmModal, type GenericConfirmModalRef } from '~/Components/GenericConfirmModal.js'
import { trpc, trpcClient, useMutationExt } from '~/Resources/TRPC.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { ImageDescriptionEditor } from './imageDescriptionEditor.js'
import { ImageLibraryImagePreview } from './ImageLibraryImagePreview.js'
import { ImageNameEditModal } from './ImageNameEditModal.js'
import { useImageLibraryUpload } from './useImageLibraryUpload'

interface ImageLibraryEditorProps {
	selectedImageName: string | null
	onDeleteImage: (imageName: string) => void
	onImageNameChanged?: (oldName: string, newName: string) => void
}

export const ImageLibraryEditor = observer(function ImageLibraryEditor({
	selectedImageName,
	onDeleteImage,
	onImageNameChanged,
}: ImageLibraryEditorProps) {
	const { imageLibrary, notifier } = useContext(RootAppStoreContext)
	const [uploading, setUploading] = useState(false)
	const [showNameEditModal, setShowNameEditModal] = useState(false)
	const [isDragOver, setIsDragOver] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const confirmModalRef = useRef<GenericConfirmModalRef>(null)

	// Get image info from the store
	const imageInfo = selectedImageName ? imageLibrary.getImage(selectedImageName) : null

	const deleteMutation = useMutationExt(trpc.imageLibrary.delete.mutationOptions())
	const { uploadImageFile } = useImageLibraryUpload()

	const handleDelete = useCallback(() => {
		if (!selectedImageName) return

		confirmModalRef.current?.show(
			'Delete Image',
			'Are you sure you want to delete this image? This action cannot be undone.',
			'Delete',
			() => {
				deleteMutation
					.mutateAsync({ imageName: selectedImageName })
					.then(() => {
						onDeleteImage(selectedImageName)
					})
					.catch((err) => {
						console.error('Failed to delete image:', err)
					})
			}
		)
	}, [deleteMutation, selectedImageName, onDeleteImage])

	const handleDownload = useCallback(() => {
		if (!selectedImageName || !imageInfo) return

		// Get image data and download
		trpcClient.imageLibrary.getData
			.query({
				imageName: selectedImageName,
				type: 'original',
			})
			.then((imageData) => {
				if (imageData?.image) {
					const subtype = imageInfo.mimeType.split('/')[1]?.split('+')[0] // e.g. 'svg+xml' -> 'svg'
					const ext = subtype === 'jpeg' ? 'jpg' : subtype || 'png'

					// Create download link
					const link = document.createElement('a')
					link.href = imageData.image
					link.download = `${imageInfo.name}.${ext}`
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
				}
			})
			.catch((err) => {
				console.error('Failed to download image:', err)
			})
	}, [selectedImageName, imageInfo])

	const uploadFile = useCallback(
		async (file: File) => {
			if (!selectedImageName) return

			setUploading(true)

			try {
				await uploadImageFile(file, selectedImageName)
			} catch (err) {
				console.error('Failed to replace image:', err)
			} finally {
				setUploading(false)
			}
		},
		[uploadImageFile, selectedImageName]
	)

	const handleReplaceImage = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.currentTarget.files?.[0]
			event.currentTarget.value = '' // Reset file input

			if (!file) return

			void uploadFile(file)
		},
		[uploadFile]
	)

	const handleImageNameChanged = useCallback(
		(oldName: string, newName: string) => {
			setShowNameEditModal(false)
			if (onImageNameChanged) {
				onImageNameChanged(oldName, newName)
			}
		},
		[onImageNameChanged]
	)

	const handleCopyVariableValue = useCallback(() => {
		notifier.show('Copied', 'Copied to clipboard', 5000)
	}, [notifier])

	const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()
		setIsDragOver(true)
	}, [])

	const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()
		setIsDragOver(false)
	}, [])

	const handleDrop = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			event.preventDefault()
			event.stopPropagation()
			setIsDragOver(false)

			const files = event.dataTransfer.files
			if (files && files.length > 0) {
				const file = files[0]
				// Check if it's an image file
				if (file.type.startsWith('image/')) {
					void uploadFile(file)
				} else {
					notifier.show('Invalid File', 'Please drop an image file', 5000)
				}
			}
		},
		[uploadFile, notifier]
	)

	const formatDate = (timestamp: number) => {
		return new Date(timestamp).toLocaleString()
	}

	const descriptionFieldId = useId()

	if (!selectedImageName) {
		return (
			<div className="image-library-editor">
				<CAlert color="info">Select an image from the library to view and edit its properties.</CAlert>
			</div>
		)
	}

	if (!imageInfo) {
		return (
			<div className="image-library-editor">
				<CAlert color="danger">Failed to load image data.</CAlert>
			</div>
		)
	}

	return (
		<div className="image-library-editor">
			<GenericConfirmModal ref={confirmModalRef} />

			<ImageNameEditModal
				visible={showNameEditModal}
				onClose={() => setShowNameEditModal(false)}
				imageName={selectedImageName}
				currentName={imageInfo.name}
				onNameChanged={handleImageNameChanged}
			/>

			<div className="mb-3">
				<div className="d-flex flex-wrap gap-2">
					<CButton color="danger" onClick={handleDelete} title="Delete Image">
						<FontAwesomeIcon icon={faTrashAlt} />
					</CButton>

					<CButton color="secondary" onClick={handleDownload}>
						<FontAwesomeIcon icon={faDownload} /> Download
					</CButton>

					<CButton color="warning" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
						<FontAwesomeIcon icon={faUpload} />
						{uploading ? ' Replacing...' : ' Replace'}
					</CButton>
				</div>

				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					onChange={handleReplaceImage}
					style={{ display: 'none' }}
				/>
			</div>

			<CForm className="row mb-3">
				<CFormLabel className="col-sm-4 col-form-label col-form-label-sm">Name</CFormLabel>
				<CCol sm={8} className="d-flex align-items-center justify-content-between">
					<div className="d-flex align-items-center">
						<span className="font-monospace">{imageInfo.name}</span>
						<CopyToClipboard text={`$(image:${imageInfo.name})`} onCopy={handleCopyVariableValue}>
							<CButton size="sm" title="Copy variable name">
								<FontAwesomeIcon icon={faCopy} />
							</CButton>
						</CopyToClipboard>
					</div>
					<CButton color="secondary" size="sm" onClick={() => setShowNameEditModal(true)} title="Edit Image name">
						<FontAwesomeIcon icon={faEdit} />
					</CButton>
				</CCol>
			</CForm>
			<CForm className="row mb-3">
				<CFormLabel htmlFor={descriptionFieldId} className="col-sm-4 col-form-label col-form-label-sm">
					Description
				</CFormLabel>
				<CCol sm={8}>
					<ImageDescriptionEditor
						id={descriptionFieldId}
						imageName={selectedImageName}
						currentName={imageInfo.description}
					/>
				</CCol>
			</CForm>

			<div
				className={`image-preview-full ${isDragOver ? 'drag-over' : ''}`}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<ImageLibraryImagePreview
					imageName={selectedImageName}
					type="original"
					checksum={imageInfo.checksum}
					alt={imageInfo.name}
				/>
				{isDragOver && (
					<div className="drag-overlay">
						<div className="drag-overlay-message">Drop image to replace</div>
					</div>
				)}
			</div>

			<div className="image-properties">
				<div className="image-metadata">
					<div className="metadata-row">
						<span className="metadata-label">Type:</span>
						<span>{imageInfo.mimeType}</span>
					</div>
					<div className="metadata-row">
						<span className="metadata-label">Modified:</span>
						<span>{formatDate(imageInfo.modifiedAt)}</span>
					</div>
				</div>
			</div>
		</div>
	)
})
